"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import {
  AlertCircle,
  RefreshCw,
  Home,
  Clock,
  WifiOff,
  Shield,
} from "lucide-react";

type ErrorType = "timeout" | "network" | "auth" | "unknown";

function getErrorDetails(error: Error & { digest?: string }): {
  type: ErrorType;
  title: string;
  description: string;
  icon: typeof AlertCircle;
} {
  const message = error.message?.toLowerCase() ?? "";

  if (
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("deadline") ||
    message.includes("aborted")
  ) {
    return {
      type: "timeout",
      title: "Carga lenta",
      description:
        "La página tardó demasiado en cargar. Esto puede ser temporal.",
      icon: Clock,
    };
  }

  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("connection") ||
    message.includes("econnrefused")
  ) {
    return {
      type: "network",
      title: "Problema de conexión",
      description: "No pudimos conectar con el servidor. Verifica tu internet.",
      icon: WifiOff,
    };
  }

  if (
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("401") ||
    message.includes("403")
  ) {
    return {
      type: "auth",
      title: "Sesión expirada",
      description: "Tu sesión puede haber expirado. Intenta recargar.",
      icon: Shield,
    };
  }

  return {
    type: "unknown",
    title: "Algo salió mal",
    description:
      "Ocurrió un error inesperado. Normalmente se arregla recargando.",
    icon: AlertCircle,
  };
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const errorDetails = getErrorDetails(error);
  const IconComponent = errorDetails.icon;

  useEffect(() => {
    console.error("[Global Error]", {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });

    // Capture to PostHog and start session recording
    if (typeof window !== "undefined" && posthog.__loaded) {
      posthog.startSessionRecording({ sampling: true, linked_flag: true });
      posthog.capture("$exception", {
        $exception_message: error.message,
        $exception_type: errorDetails.type,
        $exception_stack_trace_raw: error.stack,
        $exception_source: "global-error-boundary",
        digest: error.digest,
        path: window.location.pathname,
      });
    }
  }, [error, errorDetails.type]);

  return (
    <html lang="es">
      <body className="flex min-h-screen items-center justify-center bg-gray-950 p-4">
        <div className="w-full max-w-sm space-y-6 text-center">
          {/* Icon */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
            <IconComponent className="h-8 w-8 text-gray-400" />
          </div>

          {/* Message */}
          <div>
            <h1 className="text-xl font-semibold text-gray-100">
              {errorDetails.title}
            </h1>
            <p className="mt-2 text-gray-400">{errorDetails.description}</p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => reset()}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[#c41e3a] px-4 py-2.5 font-medium text-white transition-colors hover:bg-[#a01830]"
            >
              <RefreshCw className="h-4 w-4" />
              Intentar de nuevo
            </button>
            <a
              href="/dashboard"
              className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-700 bg-gray-800 px-4 py-2.5 font-medium text-gray-200 transition-colors hover:bg-gray-700"
            >
              <Home className="h-4 w-4" />
              Ir al inicio
            </a>
          </div>

          {/* Error ID */}
          {error.digest && (
            <p className="text-xs text-gray-500">ID de error: {error.digest}</p>
          )}
        </div>
      </body>
    </html>
  );
}
